import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sparkles, Mail, Lock, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import SocialButtons from '../components/SocialButtons';

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast({ title: 'Missing fields', description: 'Please enter your email and password.' });
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast({ title: 'Welcome back!', description: 'Signed in successfully.' });
      navigate('/dashboard');
    } catch (err) {
      toast({ title: 'Sign in failed', description: err?.response?.data?.detail || 'Invalid email or password.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSocial = (provider) => {
    toast({
      title: `${provider.charAt(0).toUpperCase() + provider.slice(1)} sign-in coming soon`,
      description: 'Please use email & password for now.',
    });
  };

  return (
    <div className="min-h-screen flex bg-[#fdfaf6]">
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-10">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-400 to-rose-500 flex items-center justify-center shadow-md">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-bold text-slate-900">MyBusinessAdPro</span>
          </Link>

          <h1 className="text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">Welcome back</h1>
          <p className="mt-2 text-slate-600">Sign in to keep growing your business.</p>

          <SocialButtons onSelect={handleSocial} />

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400 font-medium">OR CONTINUE WITH EMAIL</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@business.co.uk" className="pl-10 h-11" />
              </div>
            </div>
            <div className="space-y-1.5">
              <div className="flex justify-between">
                <Label htmlFor="password">Password</Label>
                <a href="#" className="text-xs text-orange-600 font-medium hover:underline">Forgot?</a>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input id="password" type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="pl-10 pr-10 h-11" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-lg group">
              {loading ? 'Signing in…' : (<>Sign in <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" /></>)}
            </Button>
          </form>

          <p className="mt-8 text-sm text-slate-600 text-center">
            New here? <Link to="/signup" className="font-semibold text-orange-600 hover:underline">Create an account</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block flex-1 relative bg-gradient-to-br from-orange-100 via-rose-100 to-amber-100">
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="max-w-md">
            <div className="text-7xl font-extrabold text-slate-900 leading-none">Local.<br/>Loud.<br/><span className="bg-gradient-to-r from-orange-500 to-rose-500 bg-clip-text text-transparent">Loved.</span></div>
            <p className="mt-6 text-lg text-slate-700">Join 850+ UK businesses using AI to fill their tables, books and tills.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
